import React, { useState, useEffect } from 'react';
import { apiClient } from '@/App';
import { toast } from 'sonner';
import { 
  CreditCard, CheckCircle2, Clock, XCircle, Send, 
  Calendar, User, Mail, Euro, FileText, RefreshCw,
  Building2, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const HistoriquePaiements = ({ user }) => {
  const [paiements, setPaiements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPaiements();
  }, []);

  const fetchPaiements = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/services/paiements');
      setPaiements(response.data.paiements || []);
    } catch (error) {
      console.error('Erreur chargement paiements:', error);
      if (error.response?.status === 403) {
        toast.error("Accès réservé au président et au trésorier");
      } else {
        toast.error("Erreur lors du chargement des paiements");
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'sent':
        return <Send className="w-5 h-5 text-blue-500" />;
      case 'delivered':
        return <CheckCircle2 className="w-5 h-5 text-emerald-600" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-amber-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'sent':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'delivered':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'cancelled':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-amber-100 text-amber-700 border-amber-200';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculer les stats
  const totalPaye = paiements.filter(p => p.status === 'paid' || p.status === 'sent' || p.status === 'delivered')
    .reduce((sum, p) => sum + (p.montant_ttc || 0), 0);
  const nbPaiements = paiements.length;
  const nbEnAttente = paiements.filter(p => p.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-emerald-600" />
            Historique des paiements
          </h1>
          <p className="text-slate-500 mt-1">Suivi de tous les paiements de services</p>
        </div>
        <Button onClick={fetchPaiements} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-100 rounded-lg">
              <Euro className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total payé</p>
              <p className="text-2xl font-bold text-slate-900">{totalPaye.toFixed(2)} €</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Nombre de paiements</p>
              <p className="text-2xl font-bold text-slate-900">{nbPaiements}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-lg">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">En attente</p>
              <p className="text-2xl font-bold text-slate-900">{nbEnAttente}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des paiements */}
      {paiements.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <CreditCard className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucun paiement</h3>
          <p className="text-slate-500">Les paiements effectués apparaîtront ici</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Date</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Service</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Destinataire</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Montant</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paiements.map((paiement) => (
                  <tr key={paiement.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-700">{formatDate(paiement.paid_at || paiement.created_at)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-emerald-500" />
                        <span className="font-medium text-slate-900">{paiement.type_label}</span>
                      </div>
                      {paiement.copropriete_nom && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                          <Building2 className="w-3 h-3" />
                          {paiement.copropriete_nom}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-700">{paiement.debiteur_nom || '-'}</span>
                      </div>
                      {paiement.debiteur_email && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                          <Mail className="w-3 h-3" />
                          {paiement.debiteur_email}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-lg font-bold text-emerald-600">
                        {paiement.montant_ttc?.toFixed(2)} €
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusColor(paiement.status)}`}>
                        {getStatusIcon(paiement.status)}
                        {paiement.status_label}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Note */}
      <p className="text-center text-sm text-slate-400 mt-6">
        Seuls le président et le trésorier peuvent accéder à cette page
      </p>
    </div>
  );
};

export default HistoriquePaiements;
